import { useState, useEffect, type FormEvent } from 'react'
import { Eye, EyeOff, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react'
import { auth, backendClient } from '../utils/backend'
import { useAuthStore } from '../stores'
import { isStrongPassword } from '../utils/security'

interface ResetPasswordPageProps {
  onBackToLogin?: () => void
}

function ResetPasswordPage({ onBackToLogin }: ResetPasswordPageProps) {
  const { clearRecoveryState } = useAuthStore()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [exchanging, setExchanging] = useState(true)

  // Handle PKCE code exchange on mount
  useEffect(() => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get('code')

    if (code) {
      setExchanging(true)
      backendClient.auth.exchangeCodeForSession(code)
        .then(() => {
          // Clean the URL
          window.history.replaceState(null, '', '/reset-password')
          setExchanging(false)
        })
        .catch((err) => {
          console.error('Failed to exchange code:', err)
          setError('Invalid or expired reset link. Please request a new one.')
          setExchanging(false)
        })
    } else {
      setExchanging(false)
    }
  }, [])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    if (!password) {
      setError('Password is required')
      return
    }

    if (!confirmPassword) {
      setError('Please confirm your new password')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (!isStrongPassword(password)) {
      setError('Password must be 8+ chars with uppercase, lowercase, and numbers')
      return
    }

    setLoading(true)
    try {
      await auth.updateUserPassword(password)
      clearRecoveryState()
      setSuccess(true)
      setConfirmPassword('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  if (exchanging) {
    return (
      <div className="min-h-screen bg-[#041016] flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Loader2 className="mx-auto animate-spin text-[#7cff01]" size={40} />
          <p className="text-white font-bold">Verifying reset link...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#041016] px-4 py-10 text-white">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[32px] border border-white/10 bg-[#07141a] shadow-2xl shadow-black/40 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative hidden overflow-hidden border-r border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(124,255,1,0.14),_transparent_35%),linear-gradient(160deg,_rgba(10,24,31,0.98),_rgba(4,16,22,1))] p-10 lg:flex lg:flex-col lg:justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#7cff01]">
                Secure recovery
              </p>
              <h1 className="mt-4 max-w-lg text-5xl font-black leading-tight">
                Choose a new password and get back into your account.
              </h1>
              <p className="mt-4 max-w-md text-sm leading-6 text-slate-300">
                Use the reset link from your email inbox to open this page, then create a
                fresh password that meets the security requirements.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-slate-300">
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-400">Step 1</p>
                <p className="mt-3">Open the password reset email and click the link.</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-400">Step 2</p>
                <p className="mt-3">Enter a new password and submit it here.</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 lg:p-10">
            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-[0.25em] text-slate-300 transition hover:bg-white/10 hover:text-white"
            >
              <ArrowLeft size={14} />
              Back to login
            </button>

            <div className="mt-8">
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#7cff01]">
                Reset password
              </p>
              <h2 className="mt-3 text-3xl font-black text-white">
                {success ? 'Password updated' : 'Create a new password'}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-400">
                {success
                  ? 'Your password has been saved. You can now continue to your account.'
                  : 'Enter a strong new password to complete the recovery process.'}
              </p>
            </div>

            {success ? (
              <div className="mt-8 space-y-4">
                <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                    <p>
                      Password updated successfully. Please continue to your dashboard or sign in again.
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => window.location.replace('/')}
                    className="rounded-2xl bg-[#7cff01] px-4 py-3 text-sm font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a]"
                  >
                    Go to dashboard
                  </button>
                  <button
                    type="button"
                    onClick={onBackToLogin}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-black uppercase tracking-widest text-white transition hover:bg-white/10"
                  >
                    Back to login
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                {error && (
                  <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {error}
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 pr-12 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-white"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Use at least 8 characters with uppercase, lowercase, and numbers.
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                    Confirm password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-2xl bg-[#7cff01] px-4 py-3 font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
                >
                  {loading ? 'Updating...' : 'Update password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
